/** Presets de supports palettes. Dimensions en mm, tare indicative en kg. */
export type PalletPresetId = 'EUR' | 'EUR2' | 'HALF' | 'CUSTOM'

export interface PalletPreset {
  id: PalletPresetId
  label: string
  /** Type enregistré dans le contrat produit/optimisation existant. */
  palletType: 'EUR' | 'EUR2' | 'CUSTOM'
  dimensions: { length: number; width: number } | null
  boardHeightMm: number
  emptyWeightKg: number
  /** Référence des dimensions, de la hauteur et de la tare ; aucune charge nominale n'est déduite. */
  sourceUrl?: string
}

export const PALLET_PRESETS: Record<PalletPresetId, PalletPreset> = {
  EUR: {
    id: 'EUR', label: 'EUR — 800×1200 mm', palletType: 'EUR',
    dimensions: { length: 1200, width: 800 }, boardHeightMm: 144, emptyWeightKg: 25,
    sourceUrl: 'https://www.epal-pallets.org/eu-en/load-carriers/epal-euro-pallet',
  },
  EUR2: {
    id: 'EUR2', label: 'EUR2 — 1000×1200 mm', palletType: 'EUR2',
    dimensions: { length: 1200, width: 1000 }, boardHeightMm: 162, emptyWeightKg: 35,
    sourceUrl: 'https://www.epal-pallets.org/fileadmin/user_upload/ntg_package/images/Produktdownloads/Produktdatenbla__tter/F/230309_EPAL2_Produktdatenblatt_FR.pdf',
  },
  HALF: {
    id: 'HALF', label: 'Demi-palette — 600×800 mm', palletType: 'CUSTOM',
    dimensions: { length: 800, width: 600 }, boardHeightMm: 144, emptyWeightKg: 9.5,
    sourceUrl: 'https://www.epal-pallets.org/eu-en/load-carriers/epal-6-half-pallet',
  },
  // Valeurs génériques historiques du moteur pour un support personnalisé.
  CUSTOM: {
    id: 'CUSTOM', label: 'Personnalisé', palletType: 'CUSTOM',
    dimensions: null, boardHeightMm: 150, emptyWeightKg: 20,
  },
}

export interface PalletSpecificationInput {
  pallet_type?: string | null
  pallet_custom_length?: number | null
  pallet_custom_width?: number | null
  pallet_board_height_mm?: number | null
  pallet_empty_weight_kg?: number | null
}

export function getPalletPresets(): PalletPreset[] {
  return Object.values(PALLET_PRESETS)
}

export function getPalletPresetId(input?: PalletSpecificationInput | null): PalletPresetId {
  if (input?.pallet_type === 'CUSTOM') {
    const length = Number(input.pallet_custom_length), width = Number(input.pallet_custom_width)
    const half = PALLET_PRESETS.HALF.dimensions!
    if ((length === half.length && width === half.width) || (length === half.width && width === half.length)) return 'HALF'
    return 'CUSTOM'
  }
  return input?.pallet_type === 'EUR2' ? 'EUR2' : 'EUR'
}

/** Libellé métier depuis le type et l'empreinte enregistrés dans le résultat (mm). */
export function getPalletDisplayLabel(type?: string | null, dimensions?: { length?: number | null; width?: number | null }) {
  const normalized = String(type || '').trim().toUpperCase()
  if (normalized === 'CUSTOM') {
    const preset = getPalletPresetId({ pallet_type: 'CUSTOM',
      pallet_custom_length: dimensions?.length, pallet_custom_width: dimensions?.width })
    return preset === 'HALF' ? 'Demi-palette' : 'Palette personnalisée'
  }
  if (['EUR-6', 'EUR6', 'HALF', 'DEMI'].includes(normalized)) return 'Demi-palette'
  if (['EUR-2', 'EUR2'].includes(normalized)) return 'Palette EUR-2'
  if (['EUR-3', 'EUR3'].includes(normalized)) return 'Palette EUR-3'
  if (['QUARTER', 'QUART'].includes(normalized)) return 'Quart de palette'
  if (!normalized || ['EUR', 'EUR1', 'EUR-1'].includes(normalized)) return 'Palette EUR-1'
  return `Palette ${type}`
}

/** Changer de support applique sa hauteur/tare ; Personnalisé conserve la saisie. */
export function palletPresetPatch(id: PalletPresetId): PalletSpecificationInput {
  const preset = PALLET_PRESETS[id]
  if (id === 'CUSTOM') return { pallet_type: 'CUSTOM' }
  return {
    pallet_type: preset.palletType,
    ...(preset.palletType === 'CUSTOM' && preset.dimensions
      ? { pallet_custom_length: preset.dimensions.length, pallet_custom_width: preset.dimensions.width } : {}),
    pallet_board_height_mm: preset.boardHeightMm,
    pallet_empty_weight_kg: preset.emptyWeightKg,
  }
}

/** Les valeurs saisies priment, y compris zéro. Null signifie utiliser le preset. */
export function resolvePalletSpecifications(input: PalletSpecificationInput, id = getPalletPresetId(input)) {
  const preset = PALLET_PRESETS[id]
  const fallback = PALLET_PRESETS.EUR.dimensions!
  return {
    lengthMm: (input.pallet_type === 'CUSTOM' ? input.pallet_custom_length : null) ?? preset.dimensions?.length ?? fallback.length,
    widthMm: (input.pallet_type === 'CUSTOM' ? input.pallet_custom_width : null) ?? preset.dimensions?.width ?? fallback.width,
    boardHeightMm: input.pallet_board_height_mm ?? preset.boardHeightMm,
    emptyWeightKg: input.pallet_empty_weight_kg ?? preset.emptyWeightKg,
  }
}
